import { useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { ColumnsType } from "@similarweb/pro-logic-components";

import { useTeamsTrackingService } from "../../hooks/useTeamsTrackingService";
import type { TeamListItem, TeamUser } from "../../types/teams.types";
import { teamMemberCountryLabel } from "../../utils/teamMemberCountryLabel";
import { AssignUserToTeamsModal } from "../AssignUserToTeamsModal/AssignUserToTeamsModal";
import { getAccountUserColumns } from "../TeamAccountUsersTable/accountUserColumns";
import { TeamAccountUsersTable } from "../TeamAccountUsersTable/TeamAccountUsersTable";

import { AssignToTeamButton } from "./styles";

type UnassignedUsersTableProps = {
    rows: TeamUser[];
    teams: readonly TeamListItem[];
};

export const UnassignedUsersTable = ({ rows, teams }: UnassignedUsersTableProps) => {
    const { translate } = useTranslate();
    const tracking = useTeamsTrackingService();
    const [userToAssign, setUserToAssign] = useState<TeamUser | undefined>(undefined);

    const columnConfig = useMemo((): ColumnsType<TeamUser> => {
        const baseColumns = getAccountUserColumns<TeamUser>(translate);

        if (teams.length === 0) {
            return baseColumns;
        }

        return [
            ...baseColumns,
            {
                key: "assign_to_team",
                width: 160,
                sorter: false,
                ellipsis: false,
                render: (_value, record) => (
                    <AssignToTeamButton
                        type="flat"
                        onClick={() => {
                            tracking.trackOpenAssignUserModal("unassigned_row");
                            setUserToAssign(record);
                        }}
                        dataAutomation={`teams-management-unassigned-users-assign-${record.userId}`}
                    >
                        {translate("si.management.teams.users.assign.row_action")}
                    </AssignToTeamButton>
                ),
            },
        ];
    }, [teams, tracking, translate]);

    const getSearchableValues = useMemo(
        () => (row: TeamUser) => [row.name, row.email, row.jobTitle, teamMemberCountryLabel(row.country)] as const,
        [],
    );

    return (
        <>
            <TeamAccountUsersTable
                rows={rows}
                teams={teams}
                columnConfig={columnConfig}
                searchPlaceholderKey="si.management.teams.detail.search.placeholder"
                selectionTextKey="si.management.teams.detail.selected_count"
                dataAutomation="teams-management-unassigned-users-table"
                getSearchableValues={getSearchableValues}
                assignClickSource="unassigned_bulk"
            />

            <AssignUserToTeamsModal
                isOpen={userToAssign !== undefined}
                user={userToAssign}
                teams={teams}
                onClose={() => setUserToAssign(undefined)}
            />
        </>
    );
};
